import React, { useState, useEffect, useRef } from "react";
import { Container, Card, Button, Form, InputGroup } from "react-bootstrap";
import { Send, Cpu } from "lucide-react";
import { aiChat } from "../api/client";
import { useTranslation } from "../hooks/useTranslation";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function AIChatPage() {
  const { t, locale } = useTranslation();
  
  const initialMessages = {
    de: "Willkommen! Wie kann ich dir heute helfen?",
    en: "Welcome! How can I help you today?"
  };
  
  const [chatMessages, setChatMessages] = useState([
    { sender: "ai", text: initialMessages[locale] }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Update initial message when language changes
  useEffect(() => {
    setChatMessages([
      { sender: "ai", text: initialMessages[locale] }
    ]);
  }, [locale]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    const newUserMsg = { sender: "user", text: userMessage };
    setChatMessages(prev => [...prev, newUserMsg]);
    setInput("");
    setLoading(true);

    // Add placeholder for AI message
    setChatMessages(prev => [...prev, { sender: "ai", text: "" }]);

    try {
      const messages = [
        ...chatMessages.filter(m => m.text).map(m => ({
          role: m.sender === "user" ? "user" : "assistant",
          content: m.text,
        })),
        { role: "user", content: userMessage }
      ];

      const systemPrompts = {
        de: "Du bist der fintogo Lerncoach, ein hilfreicher Assistent. Halte Antworten kurz. Antworte auf Wirtschaftsfragen nach Österreichischen Standards",
        en: "You are the fintogo learning coach, a helpful assistant. Keep answers short. Answer business questions according to Austrian standards."
      };
      const systemPrompt = systemPrompts[locale];
      
      const response = await aiChat(messages, systemPrompt, 500, true);

      if (!response) {
        throw new Error("No response body");
      }

      const reader = response.getReader();
      const decoder = new TextDecoder();
      let accumulatedText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(line => line.trim() !== '');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6); // Remove 'data: ' prefix
            
            if (data === '[DONE]') {
              continue;
            }

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices?.[0]?.delta?.content;
              
              if (content) {
                accumulatedText += content;
                
                // Update the last AI message with accumulated text
                setChatMessages(prev => {
                  const newMessages = [...prev];
                  newMessages[newMessages.length - 1] = {
                    sender: "ai",
                    text: accumulatedText
                  };
                  return newMessages;
                });
              }
            } catch (e) {
              console.error("Error parsing JSON:", e);
            }
          }
        }
      }

      // If no content was received, show error message
      if (accumulatedText === "") {
        setChatMessages(prev => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            sender: "ai",
            text: t.aiChat.noAnswer
          };
          return newMessages;
        });
      }

    } catch (error) {
      console.error("Error calling OpenRouter API:", error);
      setChatMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = {
          sender: "ai",
          text: t.aiChat.error
        };
        return newMessages;
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Container className="py-4" style={{ maxWidth: "768px" }}>
      <h2 className="mb-4">{t.aiChat.title}</h2>
      <Card className="shadow-sm d-flex flex-column" style={{ height: "65vh" }}>
        <Card.Body style={{ overflowY: "auto", padding: "1rem" }}>
          {chatMessages.map((msg, idx) => (
            <div 
              className={`d-flex mb-3 ${msg.sender === "user" ? "justify-content-end" : "justify-content-start"}`} 
              key={idx}
            >
              <div 
                className={`p-3 rounded ${msg.sender === "user" ? "bg-primary text-white" : "bg-light text-dark"}`}
                style={{ maxWidth: "70%" }}
              >
                {msg.sender === "ai" && <Cpu size={16} className="me-2" />}
                {msg.sender === "ai" ? (
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      p: ({node, ...props}) => <p style={{margin: '0.5em 0'}} {...props} />,
                      code: ({node, inline, ...props}) => (
                        inline 
                          ? <code style={{backgroundColor: '#e9ecef', padding: '0.2em 0.4em', borderRadius: '3px', color: '#d63384'}} {...props} />
                          : <code style={{display: 'block', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto', border: '1px solid #dee2e6'}} {...props} />
                      ),
                      pre: ({node, ...props}) => <pre style={{margin: '0.5em 0', backgroundColor: '#f8f9fa', padding: '0.5em', borderRadius: '3px', overflowX: 'auto'}} {...props} />,
                      ul: ({node, ...props}) => <ul style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                      ol: ({node, ...props}) => <ol style={{margin: '0.5em 0', paddingLeft: '1.5em'}} {...props} />,
                      li: ({node, ...props}) => <li style={{margin: '0.25em 0'}} {...props} />,
                      blockquote: ({node, ...props}) => <blockquote style={{margin: '0.5em 0', paddingLeft: '1em', borderLeft: '3px solid #dee2e6', color: '#6c757d'}} {...props} />,
                      h1: ({node, ...props}) => <h1 style={{fontSize: '1.5em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                      h2: ({node, ...props}) => <h2 style={{fontSize: '1.3em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                      h3: ({node, ...props}) => <h3 style={{fontSize: '1.1em', margin: '0.5em 0', fontWeight: 'bold'}} {...props} />,
                      a: ({node, ...props}) => <a style={{color: '#0d6efd'}} {...props} target="_blank" rel="noopener noreferrer" />,
                      table: ({node, ...props}) => <table style={{borderCollapse: 'collapse', margin: '0.5em 0', width: '100%'}} {...props} />,
                      th: ({node, ...props}) => <th style={{border: '1px solid #dee2e6', padding: '0.5em', backgroundColor: '#f8f9fa'}} {...props} />,
                      td: ({node, ...props}) => <td style={{border: '1px solid #dee2e6', padding: '0.5em'}} {...props} />,
                    }}
                  >
                    {msg.text || (loading && idx === chatMessages.length - 1 ? "..." : "")}
                  </ReactMarkdown>
                ) : (
                  <span>{msg.text}</span>
                )}
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </Card.Body>
        
        <Card.Footer className="p-3">
          <Form 
            onSubmit={e => {
              e.preventDefault();
              sendMessage();
            }}
          >
            <InputGroup>
              <Form.Control
                as="textarea"
                rows={1}
                placeholder={t.aiChat.messagePlaceholder}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
              />
              <Button 
                variant="primary" 
                type="submit" 
                disabled={loading || input.trim() === ""}
                className="d-flex align-items-center"
              >
                <Send size={20} />
              </Button>
            </InputGroup>
          </Form>
        </Card.Footer>
      </Card>
    </Container>
  );
}
